import { configureStore, Tuple } from '@reduxjs/toolkit';
import type { Middleware } from '@reduxjs/toolkit';
import * as tracker from '@shore/shore-tracker';

import createDebug from '../utils/debug';
import { rootReducer } from '../reducers';
import type { RootState } from '../reducers';
import * as effects from '../effects';
import { values } from '../utils/object';
import actionEventMapping from './action-event-mapping';
import { createFxMiddleware } from './fx-middleware';
import type { Fx } from './fx-middleware';
import { createGravityCalendarRedirectMiddleware } from './gravity-calendar-redirect-middleware';
import { NODE_ENV } from '../constants';
import type { IEventBus, TrackMethod } from '@shore/types';

const debug = createDebug('store');

interface StoreDeps {
  trackMethod?: TrackMethod;
  eventBus?: Pick<IEventBus, 'emit'>;
  [key: string]: unknown;
}

type DeepPartial<T> = {
  [K in keyof T]?: T[K] extends object ? DeepPartial<T[K]> : T[K];
};
type PartialState = DeepPartial<RootState>;

function createAppStore(deps: StoreDeps, initialState: PartialState = {}) {
  debug('creating store');

  const fxList = values(effects) as Fx<StoreDeps>[];
  const fxMiddleware = createFxMiddleware(fxList, deps);
  const { trackMethod, eventBus } = deps;
  /* TODO: remove this comment once calendar-v2 fully released
   * right now it is placed before fxMiddleware, so effects receive the rewritten url. It's used to show calendar-v2 to WSB merchants and to merchants created since GRAVITY_CALENDAR_START_DATE
   */
  const middlewares: Middleware[] = eventBus
    ? [createGravityCalendarRedirectMiddleware(eventBus), fxMiddleware]
    : [fxMiddleware];

  if (trackMethod) {
    tracker.initialize({ trackMethod });
    middlewares.push(
      tracker.createReduxMiddleware(actionEventMapping) as Middleware
    );
  }

  return configureStore({
    reducer: rootReducer,
    // Tuple replaces all RTK default middleware (thunk, serializable, immutable)
    // matching the original applyMiddleware-only behaviour
    middleware: () => new Tuple(...middlewares),
    devTools: NODE_ENV === 'development',
    preloadedState: initialState as Partial<RootState>,
  });
}

export type AppStore = ReturnType<typeof createAppStore>;
export type AppDispatch = AppStore['dispatch'];

export default createAppStore;
